import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import { useGetBranchListApi } from 'apis/restaurants/use-get-branch-list-api';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';

const endpoint = 'ums/v1/restaurants/branches';
export const toggleBranchActivationEndpointIdentifier = endpoint;

interface BodyType {
  isActive: boolean;
}

type TData = Record<string, any>;
type TError = AxiosError;

type TVariables = {
  branchId: string;
  body: BodyType;
};

type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useToggleBranchActivationApi(restaurantId: string, options?: Options): ReturnType {
  const { refetch: refetchBranches } = useGetBranchListApi(restaurantId, { enabled: false });
  const toast = useCustomToast();
  let fullEndpoint;

  const mutationFn = async ({ branchId, body }: TVariables) => {
    try {
      fullEndpoint = `${endpoint}/${restaurantId}/${branchId}/activation/toggle`;
      const { data } = await http.patch(fullEndpoint, body);

      // Refetch branches list
      refetchBranches();

      toast({
        description: `Branch has been ${body.isActive ? 'activated' : 'deactivated'} successfully`,
        status: 'success',
      });

      return data;
    } catch (error: any) {
      if (!error.response.status || !error.response.data.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, { mutationKey: fullEndpoint, ...options });
}
